/**
 * 置顶横幅 + 全部置顶 sheet（14.10b，两端共用）。
 *
 * - 横幅走文档流挤占列表高度（14.9 选择条同策：覆盖层会遮行，文档流进出由
 *   VirtualList 容器变高既有路径处理）；pins 空/未加载不渲染。
 * - 点击横幅＝跳转当前条（ensureMessageLoaded+tryJump 由调用方 onJump 提供；
 *   跳不过去时调用方用这里随手带的快照弹窗展示原消息，21ah）并轮播到更早一条
 *   （口径见 pinBannerModel）；右侧按钮开全部置顶 sheet。
 * - sheet：移动=底部动作单（MessageMenu 先例）/桌面=锚定横幅下方的浮层；
 *   点行=跳转并关、逐条「取消置顶」=onUnpin（乐观，列表随 chat/pins 推送更新，
 *   清空自动关）。z-index 28/29：压过输入栏（26/27，否则移动端 sheet
 *   贴底弹出会被输入栏盖掉大半，21an），仍低于消息菜单（30/31）——sheet 开着
 *   长按不冲突（sheet 先关）。桌面滚动/resize 关 sheet（坐标锚定失效，菜单同口径）。
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { PinnedMessage } from '~/worker/contract';
import { advancePinCursor, clampPinCursor, pinPreviewText } from './pinBannerModel';
import type { JumpMissingSource } from './JumpMissingDialog';
import styles from './pinBanner.module.scss';

/**
 * 置顶行手上的原消息快照（跳转失败时弹窗直出，不为正文再发请求）。
 * pin_list 行带着原消息的 msgType + 正文 envelope（14.10a 实锤，与时间线行
 * 的 msg 同形），所以弹窗能按真实类型渲染原样气泡；text 只是兜底预览。
 */
function pinJumpSource(pin: PinnedMessage): JumpMissingSource {
  return {
    name: pin.nickname,
    msgType: pin.msgType,
    content: pin.content,
    text: pinPreviewText(pin),
  };
}

export function PinBanner({
  pins,
  mobile,
  onJump,
  onUnpin,
}: {
  /** 非空（空列表由调用方不渲染本组件） */
  pins: readonly PinnedMessage[];
  mobile: boolean;
  /** 第二参=原消息快照：跳不过去时由调用方弹窗展示（21ah） */
  onJump: (msgId: number, source: JumpMissingSource) => void;
  onUnpin: (msgId: number) => void;
}) {
  const [cursor, setCursor] = useState(pins.length - 1);
  /** null=关；desktop 记打开时的锚定位（横幅右缘下方） */
  const [sheet, setSheet] = useState<{ top: number; right: number } | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const cur = pins[clampPinCursor(cursor, pins.length)]!;

  // pins 清空由调用方卸载组件；桌面滚动/resize 关 sheet（锚定坐标失效）
  useEffect(() => {
    if (sheet === null || mobile) return;
    const close = (): void => setSheet(null);
    window.addEventListener('scroll', close, { capture: true });
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, { capture: true });
      window.removeEventListener('resize', close);
    };
  }, [sheet, mobile]);

  useEffect(() => {
    if (sheet === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setSheet(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet]);

  const openSheet = (): void => {
    const rect = barRef.current?.getBoundingClientRect();
    setSheet(
      rect !== undefined
        ? { top: rect.bottom + 4, right: window.innerWidth - rect.right + 8 }
        : { top: 52, right: 8 },
    );
  };

  return (
    <div ref={barRef} className={styles.bar} data-testid="pin-banner" data-count={pins.length}>
      <button
        type="button"
        className={styles.current}
        data-testid="pin-banner-current"
        data-msg-id={cur.msgId}
        onClick={() => {
          onJump(cur.msgId, pinJumpSource(cur));
          setCursor(advancePinCursor(cursor, pins.length));
        }}
      >
        <span className={styles.title}>
          置顶消息{pins.length > 1 ? ` ${clampPinCursor(cursor, pins.length) + 1}/${pins.length}` : ''}
        </span>
        <span className={styles.preview} data-testid="pin-banner-text">
          {pinPreviewText(cur)}
        </span>
      </button>
      <button
        type="button"
        className={styles.listButton}
        data-testid="pin-banner-all"
        aria-label="全部置顶"
        onClick={() => (sheet !== null ? setSheet(null) : openSheet())}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
        </svg>
      </button>
      {sheet !== null && (
        <>
          <div className={styles.backdrop} data-testid="pin-sheet-backdrop" onClick={() => setSheet(null)} />
          <div
            className={mobile ? styles.sheetMobile : styles.sheetDesktop}
            data-testid="pin-sheet"
            style={mobile ? undefined : { top: `${sheet.top}px`, right: `${sheet.right}px` }}
          >
            <div className={styles.sheetHead}>共 {pins.length} 条置顶消息</div>
            <div className={styles.sheetBody}>
              {pins.map((p) => (
                <div key={p.msgId} className={styles.row} data-testid="pin-sheet-row" data-msg-id={p.msgId}>
                  <button
                    type="button"
                    className={styles.rowMain}
                    data-testid="pin-sheet-jump"
                    onClick={() => {
                      setSheet(null);
                      onJump(p.msgId, pinJumpSource(p));
                    }}
                  >
                    <span className={styles.rowName}>{p.nickname !== '' ? p.nickname : '消息'}</span>
                    <span className={styles.rowText}>{pinPreviewText(p)}</span>
                  </button>
                  <button
                    type="button"
                    className={styles.rowUnpin}
                    data-testid="pin-sheet-unpin"
                    onClick={() => onUnpin(p.msgId)}
                  >
                    取消置顶
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
